import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Visibility, People, ThumbUp, Comment, Share, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import TagSummary from '../../molecules/TagSummary';
import TagChip from '../../atoms/TagChip/TagChip';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import AddTagButton from '../../molecules/AddTagButton/AddTagButton';
import Card from '../../atoms/Card/Card';
import ArticleImage from '../../atoms/ArticleImage/ArticleImage';
import withArticleTags from '../../hoc/withArticleTags';
import ActionButtons from '../../molecules/ActionButtons/ActionButtons';
import { articleCardPropTypes, articleCardDefaultProps } from './ArticleCardProps';
import useCardBehavior from './useCardBehavior';
import { operationalElevation, operationalRadii, operationalSurfaces } from '../../../design-system/tokens';
import {
  isQualitativeAnalysisAiApplied,
  QUALITATIVE_AI_ACCENT
} from '../../../utils/aiAnalysisStatus';
import { getEffectiveTier } from '../../../utils/effectiveTier';

/**
 * ArticleCardList - Versão em formato de lista do card de artigo
 *
 * Esta variante apresenta o artigo em formato de linha horizontal,
 * otimizada para visualização em lista e leitura rápida.
 *
 * TRANSFORMER PATTERN: Usa interface e comportamento compartilhados.
 * Qualquer nova funcionalidade adicionada ao sistema automaticamente funciona aqui.
 */
// Função para formatar números com separadores de milhares
const formatNumber = (number) => {
  if (!number || number === 0) return '0';
  return number.toLocaleString('pt-BR');
};

const ArticleCardList = ({
  article,
  onArticleClick,
  onAction,
  onToggleFavorite,
  onToggleRead,
  onUpdateSentiment,
  onTagClick,
  onDelete,
  onAddTag,
  onAddToCollection,
  onEdit,
  selectionMode = false,
  isSelected = false,
  onSelect,
  onSendNewsletter,
  className = '',
  onTagRemove,
  ...props
}) => {
  const { renderTags, onAddTags, sx: externalSx, ...cardProps } = props;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const operationalSurface = isDarkMode ? operationalSurfaces.dark : operationalSurfaces.light;

  // Hook compartilhado - lógica comum para todas as variantes
  const {
    handleAction,
    handleCardClick
  } = useCardBehavior({
    article,
    selectionMode,
    onSelect,
    onArticleClick,
    onToggleFavorite,
    onToggleRead,
    onEdit,
    onAddTag,
    onAddToCollection,
    onDelete,
    onSendNewsletter
  });

  // Fallback logic for site info
  const articleUrl = article.siteUrl || article.url;
  const siteName = article.siteName || article['site-name'] || article.site_name || article.source ||
    (articleUrl ? (() => { try { return new URL(articleUrl).hostname.replace('www.', ''); } catch { return ''; } })() : '');
  const date = article.date || article.publishedAt || article.createdAt;

  // Verificar se tem métricas (igual ao card detailed)
  const hasMetrics = (article.valuation > 0) || (article.likesNumber > 0) || (article.viewsNumber > 0);
  const qualitativeAnalysisApplied = isQualitativeAnalysisAiApplied(article);
  const effectiveTier = getEffectiveTier(article);

  // Prepare list actions for the article
  const prepareActions = () => {
    const actions = [];

    // Favorite action - icon only to save space
    if (onToggleFavorite) {
      actions.push({
        label: '',
        onClick: (e) => {
          e.stopPropagation();
          onToggleFavorite(article._id);
        },
        icon: article.isFavorite ? 'favorite' : 'favorite-border'
      });
    }

    // Read status action - icon only to save space
    if (onToggleRead) {
      actions.push({
        label: '',
        onClick: (e) => {
          e.stopPropagation();
          onToggleRead(article._id);
        },
        icon: article.isRead ? 'read' : 'unread'
      });
    }

    return actions;
  };

  // Actions preparadas para a variante list
  prepareActions();

  return (
    <Card
      variant="list"
      selected={isSelected}
      onClick={handleCardClick}
      className={`article-card article-card--list ${className}`}
      sx={{
        cursor: selectionMode ? 'pointer' : 'default',
        borderRadius: `${operationalRadii.surface}px`,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: operationalElevation.surface,
        transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
        height: '140px !important',
        minHeight: '140px !important',
        maxHeight: '140px !important',
        overflow: 'hidden !important',
        ...(externalSx || {})
      }}
      {...cardProps}
    >
      <Box sx={{ display: 'flex', gap: 0, height: '100%' }}>
        {/* Imagem lateral - 220px */}
        <Box sx={{
          width: 220,
          flexShrink: 0,
          borderRadius: `${operationalRadii.surface}px 0 0 ${operationalRadii.surface}px`,
          overflow: 'hidden'
        }}>
          <ArticleImage
            src={article}
            alt={article.title}
            variant="list"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        </Box>

        {/* Separador */}
        <Box sx={{ width: 2, bgcolor: 'divider', flexShrink: 0 }} />

        {/* Conteúdo */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {/* Area superior: Conteudo + Acoes */}
          <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, flex: 1, minHeight: 0 }}>
            {/* Conteúdo principal */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'hidden' }}>
              {/* Título (maior e negrito) - Clicável para abrir link original */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1.3,
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  transition: 'color 0.2s ease'
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Impede que o click no título abra o modal
                  const originalUrl = article.siteUrl || article.url;
                  if (originalUrl) {
                    window.open(originalUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                {article.title}
              </Typography>

              {/* Linha com Fonte • Data • Tier • Tipo */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {/* Fonte */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': {
                      '& .source-text': {
                        color: 'primary.main',
                        textDecoration: 'underline'
                      }
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const domainUrl = article.domainUrl || article.channelUrl;
                    if (domainUrl) {
                      window.open(domainUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <Typography
                    className="source-text"
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    {siteName || 'Fonte desconhecida'}
                  </Typography>
                </Box>

                {/* Separador */}
                {date && <Typography variant="caption" sx={{ color: 'text.disabled' }}>•</Typography>}

                {/* Data */}
                {date && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {(() => {
                      try {
                        return new Date(date).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          timeZone: 'UTC'
                        });
                      } catch { return date; }
                    })()}
                  </Typography>
                )}

                {/* Tier */}
                {effectiveTier && (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>•</Typography>
                    <Chip
                      label={`Tier ${effectiveTier}`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.7rem',
                        '& .MuiChip-label': { px: 1 }
                      }}
                      color={
                        effectiveTier === 1 ? 'success' :
                          effectiveTier === 2 ? 'warning' :
                            effectiveTier === 3 ? 'info' : 'default'
                      }
                    />
                  </>
                )}

                {/* Plataforma */}
                {article.mediaType && (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>•</Typography>
                    <Chip
                      label={article.mediaType}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.7rem',
                        '& .MuiChip-label': { px: 1 }
                      }}
                      color={article.mediaType === 'YouTube' ? 'error' : 'primary'}
                    />
                  </>
                )}

                {qualitativeAnalysisApplied && (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>{"\u2022"}</Typography>
                    <Tooltip title="Analise por IA aplicada" placement="top" arrow>
                      <Chip
                        icon={<AutoAwesomeIcon sx={{ fontSize: '0.78rem !important', color: `${QUALITATIVE_AI_ACCENT} !important` }} />}
                        label="IA"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.68rem',
                          fontWeight: 760,
                          color: QUALITATIVE_AI_ACCENT,
                          bgcolor: 'transparent',
                          border: 0,
                          '& .MuiChip-label': { px: 0.65 },
                          '& .MuiChip-icon': { ml: 0.55, mr: -0.3 }
                        }}
                      />
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>

            {/* Ações sempre visíveis à direita */}
            <Box sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
              <ActionButtons
                article={article}
                onAction={handleAction}
                onUpdateSentiment={onUpdateSentiment}
                onDelete={onDelete ? () => onDelete(article._id) : undefined}
              />
            </Box>
          </Box>

          {/* Footer com valoração, audiência, inscritos e tags - SEMPRE MOSTRAR */}
          <Box sx={{
            backgroundColor: operationalSurface.panelRaised,
            borderTop: `1px solid ${theme.palette.divider}`,
            px: 2,
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}>
            {/* Lado esquerdo: Métricas */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0 }}>
              {/* Placeholder quando não há métricas */}
              {!hasMetrics && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.disabled',
                    fontStyle: 'italic',
                    opacity: 0.5
                  }}
                >
                  Métricas não disponíveis
                </Typography>
              )}
              {/* Valoração */}
              {article.valuation && article.valuation > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    R$ {new Intl.NumberFormat('pt-BR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(article.valuation)}
                  </Typography>
                </Box>
              )}

              {/* Visualizações - YouTube */}
              {article.mediaType === 'YouTube' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Visibility sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatNumber(article.viewsNumber || 0)}
                  </Typography>
                </Box>
              )}

              {/* Inscritos - YouTube */}
              {article.mediaType === 'YouTube' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <People sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatNumber(article.subscribersNumber || 0)}
                  </Typography>
                </Box>
              )}

              {/* Likes - Twitter, Instagram, Facebook, TikTok, Threads */}
              {(article.mediaType === 'Twitter' || article.mediaType === 'Instagram' || article.mediaType === 'Facebook' || article.mediaType === 'TikTok' || article.mediaType === 'Threads') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ThumbUp sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatNumber(article.likesNumber || 0)}
                  </Typography>
                  {/* LOG para Threads */}
                </Box>
              )}

              {/* Comments - Twitter, Instagram, Facebook, TikTok, Threads */}
              {(article.mediaType === 'Twitter' || article.mediaType === 'Instagram' || article.mediaType === 'Facebook' || article.mediaType === 'TikTok' || article.mediaType === 'Threads') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Comment sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatNumber(article.commentsNumber || 0)}
                  </Typography>
                  {/* LOG para Threads */}
                </Box>
              )}

              {/* Shares/Reposts - Threads */}
              {article.mediaType === 'Threads' && article.sharesNumber > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Share sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatNumber(article.sharesNumber || 0)}
                  </Typography>
                  {/* LOG para Threads */}
                </Box>
              )}

              {/* Seguidores - Twitter, Instagram, Facebook, Threads */}
              {(article.mediaType === 'Twitter' || article.mediaType === 'Instagram' || article.mediaType === 'Facebook' || article.mediaType === 'Threads') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <People sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatNumber(article.subscribersNumber || 0)}
                  </Typography>
                </Box>
              )}

              {/* Views - Twitter e Instagram */}
              {(article.mediaType === 'Twitter' || article.mediaType === 'Instagram') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Visibility sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {formatNumber(article.viewsNumber || 0)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Lado direito: Tags com X ou ícone para adicionar */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
              {article.tags && article.tags.length > 0 ? (
                <TagSummary
                  tags={article.tags}
                  maxWidth="400px"
                  fontSize="0.7rem"
                  onTagRemove={onTagRemove ? (tagId) => onTagRemove(article._id, tagId) : undefined}
                  onTagClick={onTagClick}
                >
                  {({ hiddenTags }) => {
                    const tooltipText = hiddenTags && hiddenTags.length > 0
                      ? hiddenTags.map(t => t.name || t.label || t._id).join(', ')
                      : 'Tags ocultas';
                    return hiddenTags.length > 0 && (
                      <Tooltip title={tooltipText} placement="top" arrow>
                        <span>
                          <TagChip
                            label={`+${hiddenTags.length}`}
                            size="small"
                            fontSize="0.7rem"
                            sx={{
                              minWidth: 0,
                              px: '4px',
                              borderRadius: `${operationalRadii.pill}px`,
                              '& .MuiChip-label': { px: '4px' }
                            }}
                          />
                        </span>
                      </Tooltip>
                    );
                  }}
                </TagSummary>
              ) : (
                onAddTag && (
                  <AddTagButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddTag(article);
                    }}
                  />
                )
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

// Usa a interface compartilhada - única fonte da verdade
ArticleCardList.propTypes = articleCardPropTypes;
ArticleCardList.defaultProps = articleCardDefaultProps;

// Memoizar para evitar re-renders desnecessários quando outros cards são selecionados
export default React.memo(withArticleTags(ArticleCardList, true));
